// src/components/dashboard/MyBrain.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';

interface MemoryOrb {
  id: string;
  category: string;
  label: string;
  count: string;
  icon: string;
  style: {
    width: string;
    height: string;
    top: string;
    left: string;
  };
}

interface MetaItem {
  value: string;
  label: string;
}

interface Section {
  title: string;
  content: React.ReactNode;
}

interface CategoryData {
  icon: string;
  title: string;
  subtitle: string;
  meta: MetaItem[];
  sections: Section[];
}

// Category data
const categoryData: Record<string, CategoryData> = {
  core: {
    icon: '',
    title: 'Your Complete Health Identity',
    subtitle: 'The central repository of your lifelong health journey. Every memory, condition, and insight converges here to create a comprehensive understanding of you.',
    meta: [
      { value: '147', label: 'Total Insights' },
      { value: '32', label: 'Years' },
      { value: '98%', label: 'Confidence' }
    ],
    sections: [
      {
        title: 'Identity Summary',
        content: (
          <MemoryCard
            age="Current Profile"
            title="John Doe"
            description="32-year-old male. Blood Type: O+. Primary concerns: Cardiovascular health, metabolic wellness. Active lifestyle with morning exercise preference."
          />
        )
      },
      {
        title: 'Active Health Focus',
        content: (
          <>
            <MemoryCard
              age="Priority 1"
              title="Cholesterol Management"
              description="LDL trending downward from 162 to 142 mg/dL over 6 months. Continue current dietary modifications."
              confidence={3}
            />
            <MemoryCard
              age="Priority 2"
              title="Blood Pressure Control"
              description="Stage 1 hypertension. Current reading: 135/88 mmHg. Lifestyle modifications in progress."
              confidence={3}
            />
          </>
        )
      }
    ]
  },
  birth: {
    icon: '',
    title: 'Birth & Early Development',
    subtitle: 'The foundation of your health journey. Records from your arrival into the world and earliest developmental milestones.',
    meta: [
      { value: '4', label: 'Records' },
      { value: '1992', label: 'Year' },
      { value: 'Chennai', label: 'Location' }
    ],
    sections: [
      {
        title: 'Birth Record',
        content: (
          <>
            <MemoryCard
              age="Day 0"
              title="Full-term Natural Delivery"
              description="Born at 39 weeks via natural delivery. Birth weight: 3.2 kg. APGAR score: 9/10. No complications reported."
              confidence={3}
              source="Apollo Hospital Birth Certificate"
            />
            <MemoryCard
              age="Week 1"
              title="Newborn Screening Complete"
              description="All metabolic screenings normal. Hearing test passed. No congenital conditions detected."
              confidence={3}
            />
          </>
        )
      },
      {
        title: 'Early Milestones',
        content: (
          <MemoryCard
            age="0-12 months"
            title="Developmental Milestones"
            description="All milestones met within normal range. First steps at 11 months. First words at 10 months. Breastfed for 8 months."
          />
        )
      }
    ]
  },
  vaccinations: {
    icon: '',
    title: 'Vaccination History',
    subtitle: 'Complete immunization record from birth to present. Your shield against preventable diseases.',
    meta: [
      { value: '18', label: 'Vaccines' },
      { value: '100%', label: 'Compliance' },
      { value: '2024', label: 'Last Updated' }
    ],
    sections: [
      {
        title: 'Childhood Immunizations',
        content: (
          <MemoryCard
            age="Birth - Age 5"
            title="Primary Series Complete"
            description="BCG, OPV (4 doses), DPT (5 doses), Hepatitis B (3 doses), MMR (2 doses), Varicella. All administered per schedule."
            confidence={3}
            source="Immunization Card - Uploaded 2020"
          />
        )
      },
      {
        title: 'Adult Immunizations',
        content: (
          <>
            <MemoryCard
              age="Age 28"
              title="Tdap Booster"
              description="Tetanus, Diphtheria, Pertussis booster administered. Next due: 2030."
            />
            <MemoryCard
              age="Age 29-30"
              title="COVID-19 Vaccination"
              description="Primary series (Pfizer) + 2 boosters. Last booster: March 2023."
            />
            <MemoryCard
              age="Annual"
              title="Influenza Vaccine"
              description="Annual flu shot. Last administered: October 2024."
            />
          </>
        )
      }
    ]
  },
  'childhood-illness': {
    icon: '',
    title: 'Childhood Illnesses',
    subtitle: 'Health events from your formative years that may influence your lifelong health patterns.',
    meta: [
      { value: '7', label: 'Events' },
      { value: '0-12', label: 'Age Range' },
      { value: 'Resolved', label: 'Status' }
    ],
    sections: [
      {
        title: 'Recorded Illnesses',
        content: (
          <>
            <MemoryCard
              age="Age 3"
              title="Chicken Pox"
              description="Natural infection before vaccine availability. Full recovery. Now immune. Note: May be susceptible to shingles later in life."
            />
            <MemoryCard
              age="Age 5"
              title="Tonsillitis - Recurring"
              description="Multiple episodes of strep throat between ages 5-8. Treated with antibiotics. Tonsillectomy NOT performed."
            />
            <MemoryCard
              age="Age 7"
              title="Fractured Wrist"
              description="Left wrist fracture from playground fall. Cast for 6 weeks. Healed completely with no lasting effects."
            />
            <MemoryCard
              age="Age 9"
              title="Penicillin Allergy Discovered"
              description="Severe allergic reaction to Amoxicillin. Hives and facial swelling. Marked as CRITICAL allergy. Alternative antibiotics required."
              source="Critical - Active in all recommendations"
            />
          </>
        )
      }
    ]
  },
  growth: {
    icon: '',
    title: 'Growth & Development',
    subtitle: 'Physical development milestones through adolescence. Height, weight, and body composition over time.',
    meta: [
      { value: '12', label: 'Records' },
      { value: '13-19', label: 'Age Range' },
      { value: "5'10\"", label: 'Final Height' }
    ],
    sections: [
      {
        title: 'Growth Timeline',
        content: (
          <EvolutionTrail
            steps={[
              { date: 'Age 18 - Final', text: 'Height: 5\'10" (178 cm) | Weight: 68 kg | BMI: 21.5', isFirst: true },
              { date: 'Age 16', text: 'Height: 5\'8" (173 cm) | Weight: 62 kg | Growth spurt phase' },
              { date: 'Age 14', text: 'Height: 5\'4" (163 cm) | Weight: 52 kg | Puberty onset' },
              { date: 'Age 12', text: 'Height: 4\'11" (150 cm) | Weight: 42 kg | Pre-adolescent' }
            ]}
          />
        )
      },
      {
        title: 'Development Notes',
        content: (
          <MemoryCard
            age="Age 14-16"
            title="Vision Changes"
            description="Myopia developed during adolescence. First prescription: -1.25 (Age 14). Current: -2.50 both eyes. Stable since age 20."
          />
        )
      }
    ]
  },
  allergies: {
    icon: '',
    title: 'Allergies & Sensitivities',
    subtitle: 'Known allergic reactions and food sensitivities. Critical information for safe treatment and dietary choices.',
    meta: [
      { value: '5', label: 'Allergies' },
      { value: '1', label: 'Severe' },
      { value: 'Active', label: 'Status' }
    ],
    sections: [
      {
        title: 'Critical Allergies',
        content: (
          <MemoryCard
            age="SEVERE"
            title="Penicillin & Related Antibiotics"
            description="Anaphylactic risk. Avoid all penicillin-class antibiotics including Amoxicillin, Ampicillin. Use Azithromycin or Fluoroquinolones as alternatives."
            confidence={3}
            source="Verified - Medical Record 1999"
            isCritical
          />
        )
      },
      {
        title: 'Food Sensitivities',
        content: (
          <>
            <MemoryCard
              age="Moderate"
              title="Lactose Intolerance"
              description="Digestive discomfort with dairy products. Can tolerate small amounts. Lactaid helps. Prefers oat milk alternatives."
              confidence={2}
              source="Chat inference - Nov 2024"
            />
            <MemoryCard
              age="Mild"
              title="Shellfish Sensitivity"
              description="Mild skin reaction to shrimp and crab. No respiratory symptoms. Can avoid without difficulty."
              confidence={2}
            />
          </>
        )
      },
      {
        title: 'Environmental',
        content: (
          <MemoryCard
            age="Seasonal"
            title="Pollen Allergy"
            description="Spring allergies to tree pollen. Symptoms: sneezing, itchy eyes. Managed with Cetirizine during peak season."
          />
        )
      }
    ]
  },
  mental: {
    icon: '',
    title: 'Mental & Emotional Health',
    subtitle: 'Psychological well-being throughout life. Stress patterns, mental health history, and coping mechanisms.',
    meta: [
      { value: '8', label: 'Records' },
      { value: 'Stable', label: 'Current' },
      { value: '2', label: 'Therapists' }
    ],
    sections: [
      {
        title: 'Mental Health History',
        content: (
          <>
            <MemoryCard
              age="Age 24-26"
              title="Anxiety Management"
              description="Work-related anxiety during career transition. Attended 12 sessions of CBT. Learned coping strategies. No medication. Resolved."
              confidence={3}
            />
            <MemoryCard
              age="Age 29"
              title="Stress & Burnout Episode"
              description="High stress period. Symptoms: insomnia, fatigue. Addressed through lifestyle changes: meditation, exercise routine, work boundaries."
            />
          </>
        )
      },
      {
        title: 'Current Patterns',
        content: (
          <MemoryCard
            age="Ongoing"
            title="Stress Triggers Identified"
            description="Deadline pressure, lack of sleep, caffeine overconsumption. Protective factors: morning exercise, meditation practice, social connection."
            source="Chat patterns - Ongoing analysis"
          />
        )
      }
    ]
  },
  lifestyle: {
    icon: '',
    title: 'Lifestyle & Habits',
    subtitle: 'Daily patterns, exercise routines, diet preferences, and behavioral insights that shape your health.',
    meta: [
      { value: '23', label: 'Insights' },
      { value: 'Active', label: 'Lifestyle' },
      { value: 'Vegetarian', label: 'Diet' }
    ],
    sections: [
      {
        title: 'Exercise Patterns',
        content: (
          <MemoryCard
            age="Current Routine"
            title="Morning Exercise Preference"
            description="Prefers 6-7 AM workouts. Mix of cardio (running, cycling) and strength training. 4-5 days per week. Rest days: usually weekends."
            confidence={2}
            source="Inferred from conversations"
          />
        )
      },
      {
        title: 'Dietary Preferences',
        content: (
          <>
            <MemoryCard
              age="Since Age 25"
              title="Vegetarian Diet"
              description="Lacto-vegetarian (eggs occasionally). High protein focus through lentils, paneer, tofu. Avoids processed foods. Moderate coffee consumption (2 cups/day)."
              confidence={3}
            />
            <MemoryCard
              age="Goal"
              title="Sodium Reduction"
              description="Actively reducing sodium intake for blood pressure management. Target: <2000mg daily. Avoiding packaged snacks and restaurant food."
            />
          </>
        )
      },
      {
        title: 'Sleep Patterns',
        content: (
          <MemoryCard
            age="Pattern"
            title="Irregular Sleep Schedule"
            description="Typical: 11:30 PM - 6:30 AM (7 hours). Weekends: variable. Reports difficulty falling asleep when stressed. No sleep aids used."
            confidence={2}
          />
        )
      }
    ]
  },
  labs: {
    icon: '',
    title: 'Laboratory Results',
    subtitle: 'Complete history of blood work, diagnostic tests, and biomarker trends over time.',
    meta: [
      { value: '34', label: 'Tests' },
      { value: '8', label: 'Panels' },
      { value: 'Dec 2024', label: 'Last Test' }
    ],
    sections: [
      {
        title: 'Latest Results - December 2024',
        content: (
          <>
            <MemoryCard
              age="Lipid Panel"
              title="Cholesterol - Improving"
              description="Total: 198 mg/dL | LDL: 142 mg/dL (borderline) | HDL: 52 mg/dL | Triglycerides: 128 mg/dL"
              confidence={3}
              showTimeline
            />
            <MemoryCard
              age="Metabolic Panel"
              title="Blood Sugar - Monitor"
              description="Fasting Glucose: 108 mg/dL (slightly elevated) | HbA1c: 5.9% (prediabetic range) | Insulin: Normal"
            />
            <MemoryCard
              age="Vitamins"
              title="Vitamin D - Supplementing"
              description="Level: 22 ng/mL (insufficient). Taking 2000 IU daily. Retest in 3 months. B12: Normal at 450 pg/mL."
            />
          </>
        )
      },
      {
        title: 'Trend Analysis',
        content: (
          <EvolutionTrail
            steps={[
              { date: 'December 2024', text: 'LDL: 142 mg/dL  -12% improvement', isFirst: true },
              { date: 'September 2024', text: 'LDL: 156 mg/dL - Diet changes started' },
              { date: 'March 2024', text: 'LDL: 162 mg/dL - First elevated reading' }
            ]}
          />
        )
      }
    ]
  },
  conditions: {
    icon: '',
    title: 'Health Conditions',
    subtitle: 'Active and historical health conditions. Diagnoses, treatments, and management strategies.',
    meta: [
      { value: '6', label: 'Conditions' },
      { value: '3', label: 'Active' },
      { value: '3', label: 'Resolved' }
    ],
    sections: [
      {
        title: 'Active Conditions',
        content: (
          <>
            <MemoryCard
              age="Since 2024"
              title="Stage 1 Hypertension"
              description="Blood pressure: 135/88 mmHg. No medication currently. Managing through: sodium reduction, exercise, stress management, weight loss goal."
              confidence={3}
              source="Lab Report - Nov 2024"
            />
            <MemoryCard
              age="Since 2024"
              title="Prediabetes"
              description="A1C: 5.9%. Fasting glucose: 108 mg/dL. Lifestyle intervention phase. Monitoring quarterly. No metformin yet."
            />
            <MemoryCard
              age="Since 2023"
              title="Vitamin D Deficiency"
              description="Level: 22 ng/mL. Supplementing with 2000 IU daily. Target: >30 ng/mL. Common in office workers with limited sun exposure."
            />
          </>
        )
      },
      {
        title: 'Resolved Conditions',
        content: (
          <MemoryCard
            age="Resolved 2018"
            title="Acid Reflux (GERD)"
            description="Managed with PPIs for 6 months, then resolved through dietary changes. Avoid: spicy food, late meals, coffee on empty stomach."
            isResolved
          />
        )
      }
    ]
  },
  preventive: {
    icon: '',
    title: 'Preventive Care',
    subtitle: 'Recommended screenings and preventive measures based on your age, gender, and risk factors.',
    meta: [
      { value: '5', label: 'Due Soon' },
      { value: 'Age 32', label: 'Current' },
      { value: '2025', label: 'Next Checkup' }
    ],
    sections: [
      {
        title: 'Upcoming Screenings',
        content: (
          <>
            <MemoryCard
              age="Due 2025"
              title="Annual Physical Exam"
              description="Comprehensive health checkup. Include: lipid panel, metabolic panel, BP check, vitamin levels. Schedule for Q1 2025."
            />
            <MemoryCard
              age="Due 2025"
              title="Dental Cleaning"
              description="Bi-annual dental checkup. Last visit: June 2024. Schedule next cleaning."
            />
            <MemoryCard
              age="Due 2025"
              title="Eye Examination"
              description="Annual eye exam for myopia monitoring. Check for any progression. Last prescription: -2.50 both eyes."
            />
          </>
        )
      },
      {
        title: 'Future Screenings (Age-Based)',
        content: (
          <>
            <MemoryCard
              age="Age 40+"
              title="Projected: Cardiac Screening"
              description="Given family history and current BP/cholesterol, cardiac stress test may be recommended at age 40."
              isFuture
            />
            <MemoryCard
              age="Age 45+"
              title="Projected: Colonoscopy"
              description="Standard screening begins at 45. Earlier if family history of colon issues emerges."
              isFuture
              opacity={0.5}
            />
          </>
        )
      }
    ]
  },
  family: {
    icon: '',
    title: 'Family Health History',
    subtitle: 'Hereditary conditions and health patterns in your family tree. Essential for risk assessment.',
    meta: [
      { value: '9', label: 'Records' },
      { value: '3', label: 'Generations' },
      { value: 'Cardio', label: 'Key Risk' }
    ],
    sections: [
      {
        title: 'Immediate Family',
        content: (
          <>
            <MemoryCard
              age="Father"
              title="Cardiovascular History"
              description="Type 2 Diabetes (diagnosed age 52). Hypertension (diagnosed age 48). Managed with medication. No cardiac events. Currently age 62."
              confidence={3}
            />
            <MemoryCard
              age="Mother"
              title="Thyroid & Cholesterol"
              description="Hypothyroidism (diagnosed age 45). High cholesterol, managed with statins. No diabetes. Currently age 58."
            />
          </>
        )
      },
      {
        title: 'Extended Family',
        content: (
          <>
            <MemoryCard
              age="Paternal Grandfather"
              title="Cardiac Event"
              description="Heart attack at age 65. Survived. Had Type 2 Diabetes. Passed at 78 (unrelated cause)."
            />
            <MemoryCard
              age="Maternal Grandmother"
              title="Osteoporosis"
              description="Developed osteoporosis in 70s. Hip fracture at 82. Consider calcium/vitamin D monitoring."
            />
          </>
        )
      },
      {
        title: 'Risk Assessment',
        content: (
          <div style={{
            background: 'var(--color-card-bg)',
            border: '1px solid var(--color-border)',
            borderLeft: '3px solid var(--color-muted)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '12px'
          }}>
            <div style={{
              fontSize: '15px',
              fontWeight: 500,
              marginBottom: '8px',
              color: 'var(--color-text)'
            }}>Inherited Risk Factors</div>
            <div style={{
              fontSize: '13px',
              color: 'var(--color-secondary)',
              lineHeight: 1.5
            }}>
              <strong>Elevated:</strong> Cardiovascular disease, Type 2 Diabetes, Hypertension, High cholesterol<br /><br />
              <strong>Moderate:</strong> Thyroid disorders<br /><br />
              <strong>Action:</strong> Current prediabetes and hypertension align with family history. Aggressive lifestyle intervention recommended.
            </div>
          </div>
        )
      }
    ]
  },
  genetics: {
    icon: '',
    title: 'Genetic Information',
    subtitle: 'DNA-based health insights. Carrier status, pharmacogenomics, and ancestry-related health factors.',
    meta: [
      { value: '3', label: 'Tests' },
      { value: '2023', label: 'Last Test' },
      { value: 'Complete', label: 'Status' }
    ],
    sections: [
      {
        title: 'Genetic Testing Results',
        content: (
          <>
            <MemoryCard
              age="23andMe 2023"
              title="Carrier Status"
              description="Not a carrier for: Cystic Fibrosis, Sickle Cell, Tay-Sachs, Beta Thalassemia. 40+ conditions screened - all negative."
              confidence={3}
            />
            <MemoryCard
              age="Health Predispositions"
              title="Genetic Risk Variants"
              description="Slightly increased risk: Type 2 Diabetes (aligns with family history). Average risk: Most cardiovascular markers. Reduced risk: Alzheimer's disease."
            />
          </>
        )
      },
      {
        title: 'Pharmacogenomics',
        content: (
          <MemoryCard
            age="Drug Response"
            title="Medication Metabolism"
            description="CYP2C19: Normal metabolizer (standard dosing for most medications). CYP2D6: Normal metabolizer. Note: Penicillin allergy is NOT genetic - it's acquired sensitivity."
          />
        )
      }
    ]
  },
  legacy: {
    icon: '',
    title: 'Health Legacy',
    subtitle: 'Future-focused health planning. End-of-life preferences, health directives, and wisdom to pass on.',
    meta: [
      { value: 'Future', label: 'Status' },
      { value: '-', label: 'Documents' },
      { value: 'Pending', label: 'Directives' }
    ],
    sections: [
      {
        title: 'Future Planning',
        content: (
          <>
            <MemoryCard
              age="To Complete"
              title="Advance Health Directive"
              description="Document your healthcare wishes for situations where you cannot communicate. Recommended to complete by age 40."
              isFuture
            />
            <MemoryCard
              age="To Consider"
              title="Organ Donor Registration"
              description="Consider registering as an organ donor. This decision can save up to 8 lives."
              isFuture
            />
          </>
        )
      },
      {
        title: 'Wisdom Repository',
        content: (
          <>
            <MemoryCard
              age="Lifetime"
              title="Health Lessons Learned"
              description="As you age, heal will help you document health wisdom to share with family - what worked, what didn't, and insights for future generations."
              isFuture
              opacity={0.6}
            />
            <button style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              padding: '16px',
              background: 'transparent',
              border: '1px dashed var(--color-border)',
              borderRadius: '12px',
              color: 'var(--color-muted)',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: 'var(--font-primary)'
            }}>
              <span>+</span> Start documenting your health legacy
            </button>
          </>
        )
      }
    ]
  }
};

// Memory orbs data
const memoryOrbs: MemoryOrb[] = [
  // Childhood Ring
  { id: 'birth', category: 'birth', label: 'Birth', count: '4 records', icon: '', style: { width: '80px', height: '80px', top: 'calc(50% - 180px)', left: 'calc(50% + 80px)' } },
  { id: 'vaccinations', category: 'vaccinations', label: 'Vaccines', count: '18 records', icon: '', style: { width: '70px', height: '70px', top: 'calc(50% - 120px)', left: 'calc(50% + 180px)' } },
  { id: 'childhood-illness', category: 'childhood-illness', label: 'Illness', count: '7 records', icon: '', style: { width: '65px', height: '65px', top: 'calc(50% - 30px)', left: 'calc(50% + 200px)' } },
  // Adolescence Ring
  { id: 'growth', category: 'growth', label: 'Growth', count: '12 records', icon: '', style: { width: '75px', height: '75px', top: 'calc(50% + 70px)', left: 'calc(50% + 240px)' } },
  { id: 'allergies', category: 'allergies', label: 'Allergies', count: '5 records', icon: '', style: { width: '85px', height: '85px', top: 'calc(50% + 170px)', left: 'calc(50% + 180px)' } },
  { id: 'mental', category: 'mental', label: 'Mental', count: '8 records', icon: '', style: { width: '75px', height: '75px', top: 'calc(50% + 240px)', left: 'calc(50% + 70px)' } },
  // Adulthood Ring
  { id: 'lifestyle', category: 'lifestyle', label: 'Lifestyle', count: '23 records', icon: '', style: { width: '80px', height: '80px', top: 'calc(50% + 260px)', left: 'calc(50% - 60px)' } },
  { id: 'labs', category: 'labs', label: 'Lab Results', count: '34 records', icon: '', style: { width: '95px', height: '95px', top: 'calc(50% + 200px)', left: 'calc(50% - 200px)' } },
  { id: 'conditions', category: 'conditions', label: 'Conditions', count: '6 records', icon: '', style: { width: '90px', height: '90px', top: 'calc(50% + 100px)', left: 'calc(50% - 280px)' } },
  // Maturity Ring
  { id: 'preventive', category: 'preventive', label: 'Preventive', count: 'Projected', icon: '', style: { width: '75px', height: '75px', top: 'calc(50% - 20px)', left: 'calc(50% - 300px)' } },
  { id: 'family', category: 'family', label: 'Family Hx', count: '9 records', icon: '', style: { width: '80px', height: '80px', top: 'calc(50% - 150px)', left: 'calc(50% - 260px)' } },
  // Wisdom Ring
  { id: 'legacy', category: 'legacy', label: 'Legacy', count: 'Future', icon: '', style: { width: '70px', height: '70px', top: 'calc(50% - 230px)', left: 'calc(50% - 140px)' } },
  { id: 'genetics', category: 'genetics', label: 'Genetics', count: '3 records', icon: '', style: { width: '75px', height: '75px', top: 'calc(50% - 230px)', left: 'calc(50% + 0px)' } }
];

// MemoryCard Component
interface MemoryCardProps {
  age: string;
  title: string;
  description: string;
  confidence?: number;
  source?: string;
  isCritical?: boolean;
  isResolved?: boolean;
  isFuture?: boolean;
  opacity?: number;
  showTimeline?: boolean;
}

function MemoryCard({ age, title, description, confidence, source, isCritical, isResolved, isFuture, opacity, showTimeline }: MemoryCardProps) {
  return (
    <div style={{
      background: 'var(--color-card-bg)',
      border: '1px solid var(--color-border)',
      borderLeft: isCritical ? '3px solid var(--color-error)' : undefined,
      borderRadius: '12px',
      padding: '20px',
      marginBottom: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative',
      opacity: opacity ?? (isResolved || isFuture ? 0.7 : 1)
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px'
      }}>
        <span style={{
          fontSize: '11px',
          color: 'var(--color-secondary)',
          background: 'var(--color-surface)',
          padding: '4px 10px',
          borderRadius: '4px'
        }}>{age}</span>
        {confidence && (
          <div style={{ display: 'flex', gap: '3px' }}>
            {[1, 2, 3].map(i => (
              <span key={i} style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: i <= confidence ? 'var(--color-text)' : 'var(--color-border)'
              }} />
            ))}
          </div>
        )}
      </div>
      <div style={{
        fontSize: '15px',
        fontWeight: 500,
        marginBottom: '8px',
        color: 'var(--color-text)'
      }}>{title}</div>
      <div style={{
        fontSize: '13px',
        color: 'var(--color-secondary)',
        lineHeight: 1.5
      }}>{description}</div>
      {showTimeline && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '12px'
        }}>
          <span style={{ fontSize: '10px', color: 'var(--color-muted)' }}>Mar</span>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--color-border)' }} />
          <span style={{ flex: 1, height: '1px', background: 'var(--color-border)' }} />
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--color-border)' }} />
          <span style={{ flex: 1, height: '1px', background: 'var(--color-border)' }} />
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--color-text)' }} />
          <span style={{ fontSize: '10px', color: 'var(--color-muted)' }}>Dec</span>
        </div>
      )}
      {source && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid var(--color-border)',
          fontSize: '11px',
          color: 'var(--color-muted)'
        }}>
          <span style={{
            width: '16px',
            height: '16px',
            borderRadius: '4px',
            background: 'var(--color-surface)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px'
          }}></span>
          <span>{source}</span>
        </div>
      )}
    </div>
  );
}

// EvolutionTrail Component
interface EvolutionStep {
  date: string;
  text: string;
  isFirst?: boolean;
}

function EvolutionTrail({ steps }: { steps: EvolutionStep[] }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      position: 'relative',
      paddingLeft: '20px'
    }}>
      <div style={{
        content: '""',
        position: 'absolute',
        left: '7px',
        top: '8px',
        bottom: '8px',
        width: '1px',
        background: 'linear-gradient(to bottom, var(--color-text), var(--color-border))'
      }} />
      {steps.map((step, index) => (
        <div key={index} style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '16px',
          padding: '12px 0',
          position: 'relative'
        }}>
          <div style={{
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            background: step.isFirst ? 'var(--color-text)' : 'var(--color-background)',
            border: `2px solid ${step.isFirst ? 'var(--color-text)' : 'var(--color-border)'}`,
            position: 'absolute',
            left: '-20px',
            top: '14px'
          }} />
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '10px',
              color: 'var(--color-muted)',
              marginBottom: '4px'
            }}>{step.date}</div>
            <div style={{
              fontSize: '13px',
              color: step.isFirst ? 'var(--color-text)' : 'var(--color-secondary)'
            }}>{step.text}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Main MyBrain Component
export const MyBrain: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isShifted, setIsShifted] = useState(false);
  const [toastText, setToastText] = useState('');
  const [showToast, setShowToast] = useState(false);
  const brainContainerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const openPanel = useCallback((category: string) => {
    setActiveCategory(category);
    setIsPanelOpen(true);
    setIsShifted(true);

    // Show toast
    const data = categoryData[category];
    if (data) {
      setToastText(data.title);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    setIsShifted(false);
    setActiveCategory(null);
  }, []);

  // Draw connection lines
  const drawConnections = useCallback(() => {
    if (!brainContainerRef.current || !svgRef.current) return;

    const container = brainContainerRef.current;
    const svg = svgRef.current;
    const centerX = container.offsetWidth / 2;
    const centerY = container.offsetHeight / 2;

    const orbs = container.querySelectorAll('.memory-orb');
    let lines = '';

    orbs.forEach(orb => {
      const orbElement = orb as HTMLElement;
      const orbRect = orbElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const orbX = orbRect.left - containerRect.left + orbRect.width / 2;
      const orbY = orbRect.top - containerRect.top + orbRect.height / 2;

      lines += `<line class="connection-line" x1="${centerX}" y1="${centerY}" x2="${orbX}" y2="${orbY}" stroke="var(--color-border)" stroke-width="1" fill="none" opacity="0.6"/>`;
    });

    svg.innerHTML = lines;
  }, []);

  useEffect(() => {
    const timer = setTimeout(drawConnections, 100);
    window.addEventListener('resize', drawConnections);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', drawConnections);
    };
  }, [drawConnections]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closePanel]);

  const activeCategoryData = activeCategory ? categoryData[activeCategory] : null;

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: 'calc(100vh - 60px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: 'var(--font-primary)',
      background: 'var(--color-background)'
    }}>
      {/* DEMO WARNING BANNER */}
      <div style={{
        backgroundColor: '#FEF3C7',
        borderBottom: '2px solid #F59E0B',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        zIndex: 200
      }}>
        <span style={{ fontSize: '20px' }}>⚠️</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: '#92400E', marginBottom: '4px' }}>
            DEMO PREVIEW
          </div>
          <div style={{ fontSize: '13px', color: '#78350F' }}>
            This feature shows example data only. Your real health data is not connected yet. Coming soon!
          </div>
        </div>
      </div>

      {/* Main Content Wrapper */}
      <div style={{
        position: 'relative',
        width: '100%',
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {/* Ambient Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'radial-gradient(ellipse at 30% 20%, rgba(128,128,128,0.02) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(128,128,128,0.015) 0%, transparent 50%)',
          pointerEvents: 'none'
        }} />

      {/* User Identity - Top Left */}
      <div style={{
        position: 'absolute',
        top: '32px',
        left: '32px',
        zIndex: 100
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: 500,
          marginBottom: '4px',
          color: 'var(--color-text)'
        }}>John Doe</div>
        <div style={{
          fontSize: '11px',
          color: 'var(--color-muted)'
        }}>Born 1992  Present Age 32</div>
      </div>

      {/* Brain Stats - Top Right */}
      <div style={{
        position: 'absolute',
        top: '32px',
        right: '32px',
        display: 'flex',
        gap: '32px',
        zIndex: 100
      }}>
        <StatBlock number="147" label="Total Memories" />
        <StatBlock number="32" label="Years Tracked" />
        <StatBlock number="12" label="Active Conditions" />
      </div>

      {/* Brain Container */}
      <div
        ref={brainContainerRef}
        style={{
          position: 'relative',
          width: '800px',
          height: '800px',
          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isShifted ? 'translateX(-200px)' : 'none'
        }}
      >
        {/* Life Stage Rings */}
        {[320, 520, 720, 920].map((size, i) => (
          <div key={i} style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: `${size}px`,
            height: `${size}px`,
            borderRadius: '50%',
            border: '1px solid var(--color-border)',
            opacity: 0.5,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none'
          }} />
        ))}

        {/* Stage Labels */}
        <StageLabel label="Childhood" style={{ top: '50%', left: '50%', transform: 'translate(320px, -200px)' }} />
        <StageLabel label="Adolescence" style={{ top: '50%', left: '50%', transform: 'translate(380px, 80px)' }} />
        <StageLabel label="Adulthood" style={{ top: '50%', left: '50%', transform: 'translate(320px, 280px)' }} />
        <StageLabel label="Maturity" style={{ top: '50%', left: '50%', transform: 'translate(-400px, 200px)' }} />
        <StageLabel label="Wisdom" style={{ top: '50%', left: '50%', transform: 'translate(-400px, -180px)' }} />

        {/* Connection Lines */}
        <svg
          ref={svgRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 1
          }}
        />

        {/* Central Core */}
        <div
          onClick={() => openPanel('core')}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '160px',
            height: '160px',
            cursor: 'pointer',
            zIndex: 100
          }}
        >
          <div className="core-blob" style={{
            width: '100%',
            height: '100%',
            background: 'var(--color-surface)',
            borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%',
            border: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            transition: 'all 0.4s ease',
            boxShadow: '0 0 80px rgba(128,128,128,0.08), inset 0 0 40px rgba(128,128,128,0.05)'
          }}>
            <BrainIcon />
            <span style={{
              fontSize: '10px',
              color: 'var(--color-muted)',
              letterSpacing: '1px'
            }}>32 years of health</span>
          </div>
        </div>

        {/* Memory Orbs */}
        {memoryOrbs.map((orb, index) => (
          <div
            key={orb.id}
            className="memory-orb"
            data-category={orb.category}
            onClick={() => openPanel(orb.category)}
            style={{
              position: 'absolute',
              ...orb.style,
              cursor: 'pointer',
              transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              textAlign: 'center',
              background: 'var(--color-surface)',
              border: activeCategory === orb.category ? '1px solid var(--color-text)' : '1px solid var(--color-border)',
              borderRadius: index % 3 === 0 ? '50% 50% 30% 70% / 70% 30% 70% 30%' : index % 2 === 0 ? '40% 60% 70% 30% / 40% 70% 30% 60%' : '60% 40% 30% 70% / 60% 30% 70% 40%',
              transform: activeCategory === orb.category ? 'scale(1.2)' : undefined,
              zIndex: activeCategory === orb.category ? 60 : undefined,
              boxShadow: activeCategory === orb.category ? '0 0 70px rgba(128,128,128,0.25)' : undefined
            }}
          >
            <span style={{ fontSize: '24px', marginBottom: '6px', opacity: 1 }}>{orb.icon}</span>
            <span style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: 'var(--color-text)',
              maxWidth: '80%',
              fontWeight: 500
            }}>{orb.label}</span>
            <span style={{
              fontSize: '9px',
              color: 'var(--color-muted)',
              marginTop: '4px'
            }}>{orb.count}</span>
          </div>
        ))}
      </div>

      {/* Detail Panel */}
      <div style={{
        position: 'fixed',
        top: 0,
        right: isPanelOpen ? 0 : '-500px',
        width: '480px',
        height: '100vh',
        background: 'var(--color-surface)',
        borderLeft: '1px solid var(--color-border)',
        transition: 'right 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 200,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {activeCategoryData && (
          <>
            <div style={{
              padding: '32px',
              borderBottom: '1px solid var(--color-border)',
              position: 'sticky',
              top: 0,
              background: 'var(--color-surface)',
              zIndex: 10
            }}>
              <button
                onClick={closePanel}
                style={{
                  position: 'absolute',
                  top: '24px',
                  right: '24px',
                  width: '36px',
                  height: '36px',
                  border: '1px solid var(--color-border)',
                  background: 'transparent',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-secondary)',
                  fontSize: '18px',
                  transition: 'all 0.3s ease',
                  fontFamily: 'var(--font-primary)',
                  fontWeight: '300'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-text)';
                  e.currentTarget.style.color = 'var(--color-text)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                  e.currentTarget.style.color = 'var(--color-secondary)';
                }}
              >×</button>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>{activeCategoryData.icon}</div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 400,
                letterSpacing: '-0.5px',
                marginBottom: '8px',
                color: 'var(--color-text)'
              }}>{activeCategoryData.title}</h2>
              <p style={{
                fontSize: '13px',
                color: 'var(--color-secondary)',
                lineHeight: 1.6
              }}>{activeCategoryData.subtitle}</p>
              <div style={{
                display: 'flex',
                gap: '24px',
                marginTop: '20px'
              }}>
                {activeCategoryData.meta.map((item, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    <div style={{
                      fontSize: '20px',
                      fontWeight: 400,
                      marginBottom: '4px',
                      color: 'var(--color-text)'
                    }}>{item.value}</div>
                    <div style={{
                      fontSize: '9px',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      color: 'var(--color-muted)'
                    }}>{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding: '24px 32px', flex: 1 }}>
              {activeCategoryData.sections.map((section, i) => (
                <div key={i} style={{ marginBottom: '32px' }}>
                  <div style={{
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '2px',
                    color: 'var(--color-muted)',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    {section.title}
                    <span style={{ flex: 1, height: '1px', background: 'var(--color-border)' }} />
                  </div>
                  {section.content}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Toast */}
      <div style={{
        position: 'fixed',
        bottom: '100px',
        right: '32px',
        padding: '16px 24px',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        transform: showToast ? 'translateX(0)' : 'translateX(120%)',
        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 300
      }}>
        <span style={{ fontSize: '20px' }}></span>
        <span style={{ fontSize: '13px', color: 'var(--color-text)' }}>
          Memory accessed: <span style={{ color: 'var(--color-secondary)' }}>{toastText}</span>
        </span>
      </div>

      {/* Keyframe Animations */}
      <style>{`
        @keyframes core-morph {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          25% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
          50% { border-radius: 50% 60% 30% 60% / 30% 60% 70% 40%; }
          75% { border-radius: 60% 40% 60% 30% / 70% 30% 50% 60%; }
        }

        .core-blob {
          animation: core-morph 12s ease-in-out infinite;
        }

        .core-blob:hover {
          transform: scale(1.08);
          box-shadow: 0 0 100px rgba(128,128,128,0.15), inset 0 0 40px rgba(128,128,128,0.08) !important;
        }

        @keyframes orb-morph {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          33% { border-radius: 40% 60% 50% 50% / 50% 50% 60% 40%; }
          66% { border-radius: 50% 50% 40% 60% / 40% 60% 50% 50%; }
        }

        .memory-orb {
          animation: orb-morph 8s ease-in-out infinite;
        }

        .memory-orb:hover {
          transform: scale(1.15) !important;
          z-index: 50 !important;
          border-color: var(--color-secondary) !important;
          box-shadow: 0 0 50px rgba(128,128,128,0.15);
          animation-play-state: paused;
          border-radius: 50% !important;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }

        .pulse-indicator {
          animation: pulse 2s infinite;
        }

        @keyframes float-particle {
          0% { transform: translateY(0) rotate(0deg); opacity: 0; }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% { transform: translateY(-800px) rotate(720deg); opacity: 0; }
        }

        .particle {
          animation: float-particle 20s infinite linear;
        }
      `}</style>
      </div>
    </div>
  );
};

// StatBlock Component
function StatBlock({ number, label }: { number: string; label: string }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{
        fontSize: '24px',
        fontWeight: 300,
        marginBottom: '2px',
        color: 'var(--color-text)'
      }}>{number}</div>
      <div style={{
        fontSize: '9px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        color: 'var(--color-muted)'
      }}>{label}</div>
    </div>
  );
}

// StageLabel Component
function StageLabel({ label, style }: { label: string; style: React.CSSProperties }) {
  return (
    <div style={{
      position: 'absolute',
      fontSize: '11px',
      textTransform: 'uppercase',
      letterSpacing: '2px',
      color: 'var(--color-secondary)',
      opacity: 0.9,
      fontWeight: 500,
      ...style
    }}>{label}</div>
  );
}

// ControlButton Component
function ControlButton({ icon, label }: { icon: string; label: string }) {
  return (
    <button style={{
      padding: '10px 20px',
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      color: 'var(--color-secondary)',
      fontSize: '12px',
      cursor: 'pointer',
      borderRadius: '8px',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontFamily: 'var(--font-primary)'
    }}>
      <span>{icon}</span> {label}
    </button>
  );
}

// BrainIcon Component
function BrainIcon() {
  return (
    <div style={{ width: '90px', height: '90px', marginBottom: '8px' }}>
      <svg viewBox="-20 0 190 190" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ color: 'var(--color-text)', opacity: 0.9 }}
          d="M129.49 114.51C129.121 116.961 128.187 119.293 126.762 121.322C125.337 123.351 123.461 125.021 121.28 126.2C120.676 126.535 120.043 126.816 119.39 127.04C120.22 138.04 102.74 142.04 93.32 139.42L96.82 151.66L87.82 151.98L72.07 129.43C66.76 130.93 60.49 131.65 56.44 125.15C56.0721 124.553 55.7382 123.935 55.44 123.3C54.4098 123.51 53.3614 123.617 52.31 123.62C49.31 123.62 44.31 122.72 41.77 120.96C39.7563 119.625 38.1588 117.75 37.16 115.55C31.75 116.29 27.16 115.02 24.16 111.88C20.36 107.97 19.28 101.51 21.26 94.58C23.87 85.33 31.81 74.91 47.59 71C48.9589 69.2982 50.5972 67.8322 52.44 66.66C62.35 60.31 78.44 59.76 90.65 65.79C95.3836 64.9082 100.27 65.376 104.75 67.14C113.53 70.43 119.91 77.31 121.11 84.3C123.487 85.5317 125.433 87.4568 126.69 89.82C129.32 94.76 129.69 99.71 127.92 103.71C129.587 107.049 130.138 110.835 129.49 114.51ZM123.01 109.31C121.612 110.048 120.056 110.434 118.475 110.434C116.894 110.434 115.338 110.048 113.94 109.31L114.67 104.46C117.75 104.76 120.26 103.8 121.57 101.83C123.04 99.64 122.81 96.39 120.95 92.9C118.87 88.99 114.38 88.37 111.89 88.34H111.73C105.49 88.34 99.13 91.89 96.56 96.52L92.82 94.73C93.5553 92.3449 94.8046 90.15 96.48 88.3C95.0376 87.0754 93.9474 85.4887 93.3217 83.703C92.696 81.9173 92.5574 79.9971 92.92 78.14L96.61 77.8C96.7789 79.302 97.4 80.7172 98.3911 81.8583C99.3822 82.9994 100.697 83.8125 102.16 84.19C105.238 82.8161 108.58 82.1335 111.95 82.19C112.43 82.19 112.89 82.24 113.36 82.27C110.969 78.0312 107.18 74.7545 102.64 73C91.56 68.7 84.09 75.37 82.38 77.67C78.26 83.19 80.9 88.41 82.91 91.8L79.61 94.8C76.736 92.314 74.8075 88.9127 74.15 85.17C69.92 86.44 64.24 86.17 61.06 80.74L64.06 78.68C67.43 81.2 72.78 80.98 75.32 77.87C75.9252 76.4949 76.6905 75.1959 77.6 74C79.044 72.093 80.7864 70.4316 82.76 69.08C74.47 66.82 62.76 67.19 55.68 71.73C53.7668 72.841 52.192 74.4517 51.1244 76.3895C50.0569 78.3274 49.5368 80.5192 49.62 82.73C49.62 86.3 52.42 91.94 56.19 92.82L54 97.07C51.5946 96.5129 49.4109 95.2487 47.73 93.44L44.48 97.58L41.23 96L44.41 87.68C43.8904 86.064 43.624 84.3774 43.62 82.68C43.628 81.3361 43.7687 79.9963 44.04 78.68C34.04 82.81 29.1 89.68 27.29 95.96C25.9 100.79 26.44 105.15 28.72 107.49C30.53 109.35 33.3 109.79 35.91 109.62L42.91 104.17L45.21 106.11L43.13 112.93C44.22 116.4 47.79 118.19 54.3 116.93C54.6375 114.169 55.7272 111.554 57.45 109.37C58.7133 107.552 60.3846 106.056 62.33 105L65.75 95.79L69.17 95.64L68.8 103.19C74.55 102.6 80.98 103.77 86.97 102.87L88.07 106.87C79.29 110.93 70.3 104.31 62.15 113.04C59.22 116.18 60.34 118.91 62.15 121.66C64.76 125.59 69.66 123.23 74.67 121.66C82.26 119.34 87.77 117.66 98.16 118.51C95.68 113.8 95.92 108.11 99.24 101.85L104.13 103.78C100.7 111.69 103.91 116.27 106.13 118.29C109.56 121.41 114.72 122.35 118.13 120.47C119.436 119.749 120.559 118.737 121.412 117.513C122.265 116.289 122.825 114.885 123.05 113.41C123.275 112.051 123.258 110.663 123 109.31H123.01Z"
        />
      </svg>
    </div>
  );
}
